import {
  ITrackStoreAllStoresCollection,
  ITrackStoreDailyMetric,
  ITrackStoreLiveStore,
  ITrackStoreLiveStoresCollection,
  ITrackStoreOnDuty,
  ITrackStoreOnDutyCollection,
  ITrackStoreStore,
  ITrackStoreStoreCard,
} from 'entities/TrackStore/TrackStore.models';

export const trackStoreTransactionItFormatter = (transactionId: string): string => {
  return `ID ${transactionId}`;
};

export const getWeekDates = (start: string = new Date().toISOString()): Date[] => {
  const weekStart = new Date(start);

  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);

    return d;
  });
};

interface ITransformedWeeklyGrid {
  sales: number[];
  transactionCount: number[];
  averageCheck: number[];
  maxQueuePerHour: number[];
  servedOnTime: number[];
}

export const mapDailyMetrics = (
  metrics: ITrackStoreDailyMetric[] = [],
  weekStart: string = new Date().toISOString()
): ITransformedWeeklyGrid => {
  const dates = getWeekDates(weekStart);

  const metricsMap = new Map<string, ITrackStoreDailyMetric>();

  metrics.forEach((item) => {
    const key = new Date(item.date).toISOString();

    metricsMap.set(key, item);
  });

  const result: ITransformedWeeklyGrid = {
    sales: [],
    transactionCount: [],
    averageCheck: [],
    maxQueuePerHour: [],
    servedOnTime: [],
  };

  dates.forEach((date) => {
    const key = date.toISOString();
    const item = metricsMap.get(key);

    result.sales.push(item?.sales.percent ?? 0);
    result.transactionCount.push(item?.transactionCount.percent ?? 0);
    result.averageCheck.push(item?.averageCheck.percent ?? 0);
    result.maxQueuePerHour.push(item?.maxQueuePerHour ?? 0);
    result.servedOnTime.push(item?.servedOnTime ?? 0);
  });

  return result;
};

export const getTrackStoreStoreCards = (
  allStoresCollection: ITrackStoreAllStoresCollection | null,
  store?: ITrackStoreStore
): ITrackStoreStoreCard[] => {
  if (!allStoresCollection) {
    return [];
  }

  return store ? allStoresCollection.data.filter((item) => item.storeCode === store.code) : allStoresCollection.data;
};

export const getTrackStoreOnDutyMap = (
  onDutyCollection: ITrackStoreOnDutyCollection | null
): Record<string, ITrackStoreOnDuty[]> => {
  if (!onDutyCollection) {
    return {};
  }

  return onDutyCollection.data.reduce((acc, item) => {
    if (!acc[item.storeCode]) {
      acc[item.storeCode] = [];
    }

    acc[item.storeCode].push(item);

    return acc;
  }, {} as Record<string, ITrackStoreOnDuty[]>);
};

export const getTrackStoreLiveStoreMap = (
  liveStoresCollection: ITrackStoreLiveStoresCollection | null
): Record<string, ITrackStoreLiveStore> => {
  if (!liveStoresCollection) {
    return {};
  }

  return liveStoresCollection.data.reduce((acc, item) => {
    acc[item.storeCode] = item;

    return acc;
  }, {} as Record<string, ITrackStoreLiveStore>);
};

// ─── Store Cards: Pinning (per-user, persisted in localStorage) ────────────

const TRACK_STORE_PINNED_STORES_KEY = 'trackStorePinnedStores';

export const getTrackStorePinnedStoreCodes = (userId?: string): string[] => {
  if (!userId) {
    return [];
  }

  try {
    const all = JSON.parse(localStorage.getItem(TRACK_STORE_PINNED_STORES_KEY) || '{}');

    return all[userId] || [];
  } catch {
    return [];
  }
};

export const setTrackStorePinnedStoreCodes = (userId: string, storeCodes: string[]): void => {
  try {
    const all = JSON.parse(localStorage.getItem(TRACK_STORE_PINNED_STORES_KEY) || '{}');

    all[userId] = storeCodes;
    localStorage.setItem(TRACK_STORE_PINNED_STORES_KEY, JSON.stringify(all));
  } catch {
    // ignore storage errors
  }
};

// Newly pinned stores are prepended, so `pinnedStoreCodes[0]` is the most recently pinned one.
export const getTrackStoreStoreCardsSortedByPin = (
  storeCards: ITrackStoreStoreCard[],
  pinnedStoreCodes: string[]
): ITrackStoreStoreCard[] => {
  if (!pinnedStoreCodes.length) {
    return storeCards;
  }

  const pinned = pinnedStoreCodes
    .map((storeCode) => storeCards.find((item) => item.storeCode === storeCode))
    .filter((item): item is ITrackStoreStoreCard => !!item);
  const rest = storeCards.filter((item) => !pinnedStoreCodes.includes(item.storeCode));

  return [...pinned, ...rest];
};
